import lineIntersect from "@turf/line-intersect";
import { lineString } from "@turf/turf";
import { FeatureCollection, LineString, Polygon } from "geojson";
import { insidePolygon } from "geolocation-utils";
import { LatLngBounds, latLng } from "leaflet";

import { Bounds, XYLocation } from "@nens/deck-gl";

// The Following function uses the turf library to calculate if lines intersect
// It takes an array of old points and a seperate new point and returns an array of points where the new point caused intersections with the old points.
/*
For example in below illistration
when 1 2 and 3 are coordinates of existing points
and 4 is the coordinate of a new points
calling "getPointsWhereNewPointInLineCrossesExistingLines ( 4, [1,2,3]) " will return the: [coordinates_of_X]


           4 (new point)
            \
             \
1_____________X_______2
               \     /
                \   /
                 \ /
                  3

*/

export const getPointsWhereNewPointInLineCrossesExistingLines = (
  newPoint: XYLocation,
  existingPoints: XYLocation[]
) => {
  // convert parameters to format that turf library understands
  const existingCoordinatesInTurfFormat = existingPoints;
  const newCoordinateInTurfFormat = newPoint;

  // if there are not at least 3 existing points a new point (less then 4th points) cannot cause intersections yet.
  // following represents a line between nodes 1 and 2 and 3:
  // 1__2__3
  // lines between 1_and_2 and 2_and_3 cannot intersect because they depart from a common point 2
  if (existingCoordinatesInTurfFormat.length > 2) {
    // get the existing line minus the last point.
    // since the last line part of the existing points and the new linepart depart from a common point (namely the last point of the existing points) they cannot intersect
    // we need to remove them because turf sees that the new line part starts where the old line ends and sees this as a intersection, but for our purposes it is not
    // remove last point of existing points
    const existingLine = lineString(existingCoordinatesInTurfFormat.slice(0, -1));
    // get the new line part from: the last point of the existing line + the new point
    const newLine = lineString([
      existingCoordinatesInTurfFormat[existingCoordinatesInTurfFormat.length - 1],
      newCoordinateInTurfFormat,
    ]);
    // use turf to calculate the intersections between the newline and the existing line
    const intersection = lineIntersect(existingLine, newLine);
    // return the array of features which contain the intersections as returned by turf's lineIntersect function
    return intersection.features;
  } else {
    return [];
  }
};

export const filterRectanglePolygonGeoJsonWithinBounds = (
  bbox: LatLngBounds | null,
  geojson: FeatureCollection<Polygon>
): FeatureCollection<Polygon> => {
  if (bbox === null) {
    return { type: "FeatureCollection", features: [] };
  }

  const north = bbox.getNorth();
  const east = bbox.getEast();
  const south = bbox.getSouth();
  const west = bbox.getWest();

  if (!(north && east && south && west)) {
    return geojson;
  }

  const bboxPolygon = [
    latLng(north, west),
    latLng(north, east),
    latLng(south, east),
    latLng(south, west),
    latLng(north, west),
  ];

  const features = geojson.features;
  const filteredFeatures = features.filter((item) => {
    if (
      insidePolygon(
        [item.geometry.coordinates[0][0][0], item.geometry.coordinates[0][0][1]],
        bboxPolygon
      )
    ) {
      /// item.geometry.coordinates[0][0]
      return true;
    }
    if (
      insidePolygon(
        [item.geometry.coordinates[0][1][0], item.geometry.coordinates[0][1][1]],
        bboxPolygon
      )
    ) {
      /// item.geometry.coordinates[0][0]
      return true;
    }
    if (
      insidePolygon(
        [item.geometry.coordinates[0][2][0], item.geometry.coordinates[0][2][1]],
        bboxPolygon
      )
    ) {
      /// item.geometry.coordinates[0][0]
      return true;
    }
    if (
      insidePolygon(
        [item.geometry.coordinates[0][3][0], item.geometry.coordinates[0][3][1]],
        bboxPolygon
      )
    ) {
      /// item.geometry.coordinates[0][0]
      return true;
    }
    return false;
  });

  return {
    type: "FeatureCollection",
    features: filteredFeatures,
  };
};

export const filterLineStringGeoJsonWithinBounds = (
  bounds: Bounds | null,
  geojson: FeatureCollection<LineString>
): FeatureCollection<LineString> => {
  if (bounds === null) {
    return { type: "FeatureCollection", features: [] };
  }

  const north = bounds[1];
  const east = bounds[0];
  const south = bounds[3];
  const west = bounds[2];

  if (!(north && east && south && west)) {
    return geojson;
  }

  const bboxPolygon = [
    latLng(north, west),
    latLng(north, east),
    latLng(south, east),
    latLng(south, west),
    latLng(north, west),
  ];

  const features = geojson.features;
  const filteredFeatures = features.filter((item) => {
    if (
      insidePolygon([item.geometry.coordinates[0][0], item.geometry.coordinates[0][1]], bboxPolygon)
    ) {
      return true;
    }
    if (
      insidePolygon([item.geometry.coordinates[1][0], item.geometry.coordinates[1][1]], bboxPolygon)
    ) {
      return true;
    }
    return false;
  });

  return {
    type: "FeatureCollection",
    features: filteredFeatures,
  };
};
